import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { RadioGroup } from "@headlessui/react";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Modal from "../../layouts/Modal";
import classNames from "../../utils/classNames";
import sortOffers from "../../utils/sortOffers";
import PriceFormat from "../../utils/priceFormat";
import openInNewTab from "../../utils/openInNewTab";
import { FormatType, OrderPaymentStatus, SaleSource } from "../../models/EbayOffer";
import Card from "../../components/shared/Card/Card";
import usePartInvoice from "../../data/usePartInvoice";
import useSinglePart from "../../data/useSinglePart";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useWilmaUsers from "../../data/useWilmaUsers";
import formatDateTime from "../../utils/formatDateTime";
import useContactSingle from "../../data/useContactsSingle";
import PartQc from "../../components/leads/LeadParts/PartQc";
import useLeadByVehicleId from "../../data/useLeadByVehicleId";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import usePartMutations from "../../data/usePartMutations";
import LocationPicker from "../../components/stock/LocationPicker";
import PartNumberEdit from "../../components/stock/PartNumberEdit";
import ContactFromId from "../../components/contacts/ContactFromId";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import AccountDetails from "../../components/accounts/AccountDetails";
import StockPrintLabels from "../../components/stock/StockPrintLabels";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import ViewOnButton from "../../components/shared/ViewOnButton";
import VehiclePartsCard from "../../components/vehicles/VehiclePartsCard";
import EbayPaymentMethod from "../../components/ebayOrders/EbayPaymentMethod";
import EbayPaymentStatus from "../../components/ebayOrders/EbayPaymentStatus";
import useNotification from "../../components/notifications/useNotifications";
import CreditInvoiceModal from "../../components/invoices/CreditInvoiceModal";
import EditListingImages from "../../components/ebayListings/EditListingImages";
import SendToQcModal from "../../components/stock/SendToQcModal";
import PartHistoryDrawer from "../../components/stock/PartHistoryDrawer";
import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import PartVatTreatmentOverride from "../../components/parts/PartVatTreatmentOverride";
import EbayPartMessages from "../../components/shared/EbayPartMessages/EbayPartMessages";
import PartProPartMessages from "../../components/shared/PartProPartMessages/PartProPartMessages";
import { IPart, IPartCancelRequest, PartStatus } from "../../models/Part";
import WarehouseLocationFromId from "../../components/warehouseLocations/WarehouseLocationFromId";
import LinkedWorkOrdersCard from "../../components/workOrders/LinkedWorkOrdersCard";
import { SearchPaletteItem } from "../../components/shared/Inputs/SearchPaletteTypes";
import UnlistedDetailsCard from "../../components/shared/Unlisted/UnlistedDetailsCard";
import WarningBar from "../../components/shared/Card/WarningBar";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import EditListing from "../../components/ebayListings/EditListing";
import ListingSidebar from "../../components/ebayListings/ListingSidebar";
import { canAccessFeature } from "../../auth/featureScopes";
import { useAuth } from "../../contexts/AuthContext";


/** Tab order for this page. The slug at each index is what ?tab= accepts. */
enum TabIndex {
  Information = 0,
  Listing = 1,
  Images = 2,
  Account = 3,
}

const TAB_SLUGS = ["information", "listing", "images", "account"];

const partStatusColour = (status: PartStatus | null | undefined) => {
  switch (status) {
    case PartStatus["On The Car"]:
      return "bg-orange-200 text-orange-500";
    case PartStatus.Damaged:
      return "bg-red-200 text-red-500";
    case PartStatus.Missing:
      return "bg-yellow-200 text-yellow-500";
    case PartStatus.QC:
      return "bg-purple-200 text-purple-500";
    case PartStatus.Dismantled:
      return "bg-green-200 text-green-500";
    case PartStatus.Sold:
      return "bg-blue-200 text-blue-500";
    default:
      return "bg-gray-200 text-gray-500";
  }
};

/** A single label/value pair inside an InfoSection. */
const InfoField = ({
  label,
  value,
  onEdit,
  className,
  children,
}: {
  label: string;
  value?: React.ReactNode;
  onEdit?: () => void;
  className?: string;
  children?: React.ReactNode;
}) => {
  const isEmpty = value === null || value === undefined || value === "";

  return (
    <div className={classNames("min-w-0", className ?? "")}>
      <div className="flex flex-row items-center gap-x-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        {onEdit && (
          <FontAwesomeIcon
            className="h-3 w-3 cursor-pointer text-gray-400 transition-colors hover:text-primary-600"
            icon={faPencil}
            onClick={onEdit}
          />
        )}
      </div>
      <div className="mt-1 break-words text-sm text-gray-900">
        {children ?? (isEmpty ? <span className="text-gray-400">&mdash;</span> : value)}
      </div>
    </div>
  );
};

/** A titled group of InfoFields. Sections are separated by the card's dividers. */
const InfoSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="px-4 py-4 sm:px-6">
    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</p>
    <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">{children}</div>
  </div>
);

const StockInformation = ({ part, onGoToListing }: { part: IPart; onGoToListing?: () => void }) => {
  const { t } = useTranslation("stock");
  const navigate = useNavigate();
  const { lead: leadQuery } = useLeadByVehicleId("StockInformation", part.vehicleId!);
  const { cancel } = useSinglePartMutations(part.id!);
  const { invoice } = usePartInvoice(part.id!);
  const { downloadInvoice } = useInvoiceMutations();
  const { contact } = useContactSingle(part.removedBy!);
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [partNumberOpen, setPartNumberOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);

  const { wilmaUsers } = useWilmaUsers();
  const [locatedBy, setLocatedBy] = useState<string | null>(null);

  const lead = useMemo(() => {
    if (!part.vehicleId) return null;
    if (leadQuery.isLoading || !leadQuery.data?.data) return null;
    return leadQuery.data?.data?.leads?.[0];

  }, [part.vehicleId, leadQuery.isLoading, leadQuery.data?.data]);

  useEffect(() => {
    if (part.locatedBy && !wilmaUsers.isLoading && wilmaUsers.data) {
      const user = wilmaUsers.data.data.find((user) => user.id === part.locatedBy);
      setLocatedBy(user ? user.fullname : t("Unknown User"));
    }
  }, [part.locatedBy, wilmaUsers.data, wilmaUsers.isLoading, t]);

  const getCancellable = () => {
    if (part.soldDate === null) return t("No - Not Sold");

    if (part.shippedOn !== null) return t("No - Already Shipped");

    if (
      new Date().getTime() - new Date(part.soldDate!).getTime() >
      7 * 24 * 60 * 60 * 1000
    )
      return t("Unlikely - Sold Too Long Ago");

    return t("Yes");
  };

  // The offer the sale happened on - part.offers is sorted by sortOffers upstream.
  const saleOffer = part.offers?.[0];

  // Shopify and PartPro sales set orderId too, but this cancels on eBay only. Treat a
  // missing saleSource as eBay so older parts that pre-date the field keep the button.
  const isEbaySale =
    part.orderId != null &&
    (saleOffer?.saleSource == null || saleOffer.saleSource === SaleSource.eBay);

  const isPaid = saleOffer?.paymentStatus === OrderPaymentStatus.Paid;

  const { watch, register, setValue, getValues } = useForm<IPartCancelRequest>({
    defaultValues: {
      cancelReason: "ADDRESS_ISSUES",
      partId: part.id!,
    },
  });

  const cancelReasonOptions = [
    { id: "ADDRESS_ISSUES", name: t("Address Issues") },
    { id: "BUYER_ASKED_CANCEL", name: t("Buyer Requested Cancellation") },
    //eBay rejects an unpaid cancellation on an order it has taken payment for
    ...(isPaid ? [] : [{ id: "ORDER_UNPAID", name: t("Order Unpaid") }]),
    {
      id: "OUT_OF_STOCK_OR_CANNOT_FULFILL",
      name: t("Out of Stock or Cannot Fulfill"),
    },
    //These four are the whole of eBay's seller-side CancelReasonEnum - the other values
    //(FOUND_CHEAPER_PRICE, ORDER_MISTAKE) are buyer-only and there is no generic "other".
  ];

  const closeCancelModal = () => {
    setCancelOpen(false);
    //Otherwise reopening the modal lands straight on the confirm step
    setConfirmCancel(false);
  };

  const doCancel = () => {
    cancel.mutate(getValues(), {
      onSuccess: (res: any) => {
        addNotification({
          variant: "success",
          primaryText: t("Order cancelled"),
          secondaryText: res?.message,
        });
        closeCancelModal();
      },
      onError: (err: any) => {
        //eBay's own wording comes through here - it's the only thing that says why
        addNotification({
          variant: "error",
          primaryText: t("Failed to cancel order"),
          secondaryText: err?.message,
        });
      },
    });
  };

  const handleGoToLead = () => {

    if (lead) {
      openInNewTab(`/leads/${lead.id}`);
    } else {
      addNotification({
        variant: "error",
        primaryText: t("Could not find associated lead"),
      });
    }
  };

  const currentPrice = useMemo(() => {
    //Sold price
    if (part.price) return part.price;

    //Has offers
    if (part.offers && part.offers.length > 0) {
      const offer = part.offers[0];
      return offer.format === FormatType.FixedPrice ? offer.buyItNowPrice : offer.auctionStartPrice;
    }

    //No offers yet
    if (part.enteredPrice) return part.enteredPrice;
  }, [part])

  return (
    <div className="flex flex-col gap-y-4">
      <Modal open={cancelOpen} setOpen={closeCancelModal} width="max-w-xl">
        <>
          <p className="text-xl font-semibold">{t("Cancel Order")}</p>
          <div className="w-full flex flex-col gap-y-4 my-4">
            <p className="flex justify-between">
              <span>{t("Title")}: </span>
              <span>{part.title}</span>
            </p>
            <p className="flex justify-between">
              <span>{t("Tag")}: </span>
              <span>{part.tagNumber}</span>
            </p>
            <p className="flex justify-between">
              <span>{t("Sold At")}: </span>
              <span>
                {formatDateTime(
                  part.soldDate,
                  DateTime.DATETIME_SHORT,
                  "Not Sold"
                )}
              </span>
            </p>
            <p className="flex justify-between">
              <span>{t("Cancellation Acceptable")}: </span>
              <span>{getCancellable()}</span>
            </p>
            <SelectInput
              label={t("Reason for Cancellation")}
              options={cancelReasonOptions}
              setValue={setValue}
              watch={watch}
              register={register}
              registerName={"cancelReason"}
            />
          </div>

          {confirmCancel ? (
            <div className="flex flex-row min-w-full gap-x-4">
              <ButtonInput
                label={t("Confirm")}
                isSubmit={false}
                onClick={() => doCancel()}
                classes={"flex-grow"}
                isLoading={cancel.isLoading}
              />
              <ButtonInput
                label={t("Close")}
                isSubmit={false}
                onClick={() => setConfirmCancel(false)}
                classes={"flex-grow"}
                disabled={cancel.isLoading}
              />
            </div>
          ) : (
            <div className="flex flex-row min-w-full">
              <ButtonInput
                label={t("Cancel order")}
                isSubmit={false}
                onClick={() => setConfirmCancel(true)}
                classes={"flex-grow"}
              />
            </div>
          )}
        </>
      </Modal>
      <SendToQcModal open={qcOpen} setOpen={setQcOpen} parts={[part]} />
      <LocationPicker open={open} setOpen={setOpen} part={part} />
      <PartNumberEdit open={partNumberOpen} setOpen={setPartNumberOpen} part={part} />
      <Card title={t("Part Information")} bodyClassName="divide-y divide-gray-100">
        <div className="flex flex-wrap items-start justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("Part Title")}
            </p>
            <p className="mt-1 text-lg font-semibold leading-6 text-gray-900">{part.title}</p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t("Part Price")}
            </p>
            <p className="mt-1 text-lg font-semibold leading-6 text-gray-900">
              {PriceFormat(currentPrice)}
            </p>
          </div>
        </div>

        <InfoSection title={t("Details")}>
          <InfoField label={t("Tag Number")} value={part.tagNumber} />
          <InfoField
            label={t("Part Number")}
            value={part.partNumber}
            onEdit={() => setPartNumberOpen(!partNumberOpen)}
          />
          <InfoField label={t("OE Part Number")} value={part.oePartNumber} />
          <InfoField label={t("Location")} onEdit={() => setOpen(!open)}>
            <WarehouseLocationFromId
              id={part.warehouseLocationId}
              lastKnownId={part.lastKnownWarehouseLocationId}
            />
          </InfoField>
          <InfoField label={t("Located By")} value={locatedBy} />
          <InfoField label={t("Priced By")}>
            {part.pricedBy ? (
              <ContactFromId contactId={part.pricedBy} />
            ) : (
              <span className="text-gray-400">&mdash;</span>
            )}
          </InfoField>
        </InfoSection>

        <InfoSection title={t("Status")}>
          <InfoField label={t("Part Status")}>
            <span
              className={classNames(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                partStatusColour(part.partStatus)
              )}
            >
              {t(PartStatus[part.partStatus])}
            </span>
          </InfoField>
          <InfoField
            label={t("Status Message")}
            value={part.statusMessage}
            className="col-span-2"
          />
          {(part.isDamaged || part.damageMessage) && (
            <InfoField label={t("Damage Reason")} className="col-span-2 sm:col-span-3">
              <p className="whitespace-pre-line">
                {part.damageMessage ?? <span className="text-gray-400">&mdash;</span>}
              </p>
            </InfoField>
          )}
          <InfoField label={t("QC Reason")} className="col-span-2 sm:col-span-3">
            <p className="whitespace-pre-line">
              {part.qcReason ?? <span className="text-gray-400">&mdash;</span>}
            </p>
          </InfoField>
          <InfoField label={t("QC Comment")} className="col-span-2 sm:col-span-3">
            <p className="whitespace-pre-line">
              {part.qcApprovedComment ?? <span className="text-gray-400">&mdash;</span>}
            </p>
          </InfoField>
        </InfoSection>

        <InfoSection title={t("Timeline")}>
          <InfoField
            label={t("Published Date")}
            value={formatDateTime(part.offers[0]?.publishedOn, DateTime.DATETIME_SHORT, "Not Published")}
          />
          <InfoField
            label={t("Listed Date")}
            value={formatDateTime(part.offers[0]?.listingStarted, DateTime.DATETIME_SHORT, "No Listed Date")}
          />
          <InfoField
            label={t("Sold Date")}
            value={formatDateTime(part.soldDate, DateTime.DATETIME_SHORT, "Not Sold")}
          />
          <InfoField
            label={t("Picked Date")}
            value={formatDateTime(part.pickedOn, DateTime.DATETIME_SHORT, "Not Picked")}
          />
          <InfoField
            label={t("Shipped Date")}
            value={formatDateTime(part.shippedOn, DateTime.DATETIME_SHORT, "Not Shipped")}
          />
          <InfoField
            label={t("Dismantled Date")}
            value={formatDateTime(part.removedOn, DateTime.DATETIME_SHORT, "Not Dismantled")}
          />
          <InfoField
            label={t("Dismantled By")}
            value={part.removedBy ? contact.data?.data?.fullname : t("Not Dismantled")}
          />
        </InfoSection>

        <InfoSection title={t("Order & Handling")}>
          <InfoField label={t("Tracking Number")} value={part.trackingNumber} />
          <InfoField label={t("Payment Status")}>
            <EbayPaymentStatus offers={part.offers} />
          </InfoField>
          <InfoField label={t("Payment Method")}>
            <EbayPaymentMethod offers={part.offers} />
          </InfoField>
          <InfoField
            label={t("Est Picking")}
            value={part.category?.pickingTime ? `${part.category.pickingTime} ${t("mins")}` : null}
          />
          <InfoField
            label={t("Est Packing")}
            value={part.category?.packingTime ? `${part.category.packingTime} ${t("mins")}` : null}
          />
        </InfoSection>

        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">

          <div className="flex flex-row flex-wrap gap-4">
            {onGoToListing && (
              <ButtonInput
                label={t("Go to Listing")}
                isSubmit={false}
                onClick={onGoToListing}
                classes={undefined}
              />
            )}
            <ButtonInput
              label={t("Send to QC")}
              isSubmit={false}
              onClick={() => setQcOpen(true)}
              classes={undefined}
            />
            {isEbaySale ? (
              <ButtonInput
                label={t("Cancel order")}
                isSubmit={false}
                onClick={() => setCancelOpen(true)}
                classes={undefined}
              />
            ) : null}
            <ButtonInput
              label={t("View Vehicle")}
              isSubmit={false}
              onClick={() => openInNewTab("/vehicles/" + part.vehicle?.id)}
              classes={undefined}
            />
            <ButtonInput
              label={t("View Lead for Vehicle")}
              isSubmit={false}
              onClick={handleGoToLead}
              classes={undefined}
            />
          </div>

          <div>
            {(part.ebayItemNumber || part.isPublishedOnShopify) ? (
              <ViewOnButton part={part} />
            ) : (
              <>
                {invoice.data?.data && (
                  <>
                    <CreditInvoiceModal
                      open={creditModalOpen}
                      setOpen={setCreditModalOpen}
                      invoice={invoice.data?.data ?? null}
                    />
                    <ButtonInput
                      label={t("Return Part")}
                      isSubmit={false}
                      onClick={() => setCreditModalOpen(true)}
                      classes={"bg-red-500 hover:bg-red-600 mr-4"}
                    />
                    <ButtonInput
                      label={t("Download Invoice")}
                      isSubmit={false}
                      onClick={() => {
                        downloadInvoice(invoice.data?.data.id!);
                      }}
                      classes={"!bg-emerald-500 hover:!bg-emerald-600"}
                    />
                  </>
                )}
              </>
            )}

            {invoice.data?.data && (
              <ButtonInput
                label={t("Download Invoice")}
                isSubmit={false}
                onClick={() => downloadInvoice(invoice.data?.data.id!)}
                classes={"!bg-emerald-500 hover:!bg-emerald-600"}
              />
            )}
          </div>

        </div>
      </Card>

      {part.vehicle && (
        <PartVehicleDetails vehicle={part.vehicle!} price={null} />
      )}

      <PartVatTreatmentOverride part={part} />

      {part.partStatus === PartStatus.QC && (
        <PartQc part={part} />
      )}

      {/* <PartsRecall /> */}

      <VehiclePartsCard vehicleId={part.vehicleId!} />

      {/* Location, tag number, general part info, published date, listed date, sold date, picked date, shipped date, tracking info */}
    </div>
  );
};

const formatPartStatus = (status: PartStatus | null | undefined, t: (key: string) => string) => {
  const classes = "px-4 max-w-36 text-xs rounded-full h-10 flex justify-center items-center absolute top-[-78px] right-0";
  const colour = partStatusColour(status);

  if (status === null || status === undefined) {
    return (
      <div className={`${classes} ${colour}`}>
        <p className="">{t("N/A")}</p>
      </div>
    )
  }

  return (
    <div className={`${classes} ${colour}`}>
      <p className="">{t(PartStatus[status])}</p>
    </div>
  )
};

export default function ViewStock() {
  const { t } = useTranslation("stock");
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { partId } = useParams();
  const { singlePart } = useSinglePart(partId!);
  const { deletePart, restorePart, returnPart } = usePartMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Part");
  const { addNotification } = useNotification();
  const { invoice } = usePartInvoice(partId!);
  const { featureScopes } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();

  const [part, setPart] = useState<IPart | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Page within the listing editor: 0 = listing form, 1 = item specifics.
  const [listingPage, setListingPage] = useState<number>(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const workOrderItems = useMemo<SearchPaletteItem[]>(() => {
    if (!part?.id) return [];
    const isSoldOrder = part.partStatus === PartStatus.Sold;
    return [
      {
        id: part.id,
        name: `${part.tagNumber ?? "Unknown Tag"} - ${part.title ?? "Part"}`,
        type: isSoldOrder ? "Order" : "Stock",
      },
    ];
  }, [part]);
  // The listing editor is an Order Management feature - hide the tab without it.
  const canManageListings = canAccessFeature(featureScopes, "orderManagement");

  const viewTypes = [
    { value: TabIndex.Information, label: t("Information") },
    ...(canManageListings ? [{ value: TabIndex.Listing, label: t("Listing") }] : []),
    { value: TabIndex.Images, label: t("Images") },
    { value: TabIndex.Account, label: t("Buyer Account") },
  ];

  // The tab lives in the URL (?tab=listing) so links can deep link straight to it -
  // this is how the old /ebay-listings/:partId links land on the listing editor.
  const viewType = useMemo(() => {
    const index = TAB_SLUGS.indexOf((searchParams.get("tab") ?? "").toLowerCase());
    if (index === -1) return TabIndex.Information;
    if (index === TabIndex.Listing && !canManageListings) return TabIndex.Information;
    return index;
  }, [searchParams, canManageListings]);

  const setViewType = (value: number) => {
    const next = new URLSearchParams(searchParams);

    if (value === TabIndex.Information) {
      next.delete("tab");
    } else {
      next.set("tab", TAB_SLUGS[value]);
    }

    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setInfo({
      name: t("Stock Details"),
      desc: t("View details of this part here"),
    });
  }, []);

  useEffect(() => {
    if (!singlePart.isLoading && singlePart.data?.data) {
      let part = singlePart.data?.data;

      // console.log(part.vehicle?.parts);

      if (part?.offers && part?.offers.length > 0) {
        part.offers.sort(sortOffers);
      }

      if (part.images && part.images.length > 0) {
        part.images.sort((a, b) => {
          if (a.order === null) {
            return 1;
          }

          if (b.order === null) {
            return -1;
          }

          return a.order - b.order;
        });
      }

      setPart(part);
      setIsLoading(false);
      return;
    }

    if (!singlePart.isLoading) {
      setPart(undefined);
      setIsLoading(false);
    }
  }, [singlePart.isLoading, singlePart.data?.data, partId, isLoading]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  // On scan-switch the URL partId changes a render before the new part loads,
  // so the old part is still in state. Never render the upload UI against a part
  // that doesn't match the scanned id, or photos upload to the previous part
  // (image mix-up bug). The data effect above repopulates once the new part loads.
  if (part && part.id !== partId) {
    return <LoadingWheel />;
  }

  if (!part) {
    return (
      <div className="mt-4">
        <Card title={t("Stock Details")} bodyClassName="p-4">
          <p>{t("Unable to load part details.")}</p>
        </Card>
      </div>
    );
  }

  const isDeletedPart = !!part?.deleted || part?.partStatus === PartStatus.Deleted;

  const handleRestorePart = () => {
    if (!part?.id) return;

    restorePart.mutate(part.id, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: t("Part restored"),
        });
        singlePart.refetch();
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: t("Failed to restore part"),
        });
      },
    });
  };

  const handleReturnPart = () => {
    if (!part?.id) return;

    returnPart.mutate(part.id, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: t("Part returned"),
        });
        setReturnOpen(false);
        singlePart.refetch();
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: t("Failed to return part"),
        });
      },
    });
  };

  const handleDeletePart = () => {
    if (!part?.id) return;

    deletePart.mutate(part.id, {
      onSuccess: (res) => {
        // The part is deleted either way, but the marketplace withdraw is best-effort. Saying
        // "deleted" when the listing is still live on eBay is what let these go unnoticed.
        if (res?.data && !res.data.listingWithdrawn) {
          addNotification({
            variant: "warn",
            primaryText: t("Part deleted, but the listing is still live"),
            secondaryText: res.message ?? res.data.withdrawFailures?.join("; "),
          });
        } else {
          deleteHandlers.onSuccess();
        }
        setDeleteOpen(false);
        navigate("/stock");
      },
      onError: () => {
        deleteHandlers.onError();
      },
    });
  };

  return (
    <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6 relative">
      <ConfirmModal
        title={t("Delete Part?")}
        message={t("Are you sure you want to delete this part?")}
        confirmButtonText={null}
        onConfirm={handleDeletePart}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        isLoading={deletePart.isLoading}
      />
      <ConfirmModal
        title={t("Return Part?")}
        message={t("This will undo the sale and reset the part's sold, picking and shipping details. Are you sure you want to return this part?")}
        confirmButtonText={null}
        onConfirm={handleReturnPart}
        open={returnOpen}
        setOpen={setReturnOpen}
        isLoading={returnPart.isLoading}
      />
      {formatPartStatus(part?.partStatus, t)}
      {isDeletedPart && (
        <div className="col-span-3">
          <WarningBar
            button={
              <ButtonInput
                label={t("Restore Part")}
                isSubmit={false}
                onClick={handleRestorePart}
                classes="bg-red-600 hover:bg-red-700"
                isLoading={restorePart.isLoading}
              />
            }
          >
            <div className="flex flex-col">
              <p className="text-sm font-semibold">{t("This part is deleted")}</p>
              <p className="text-sm">{t("Restore to put it back into stock workflows.")}</p>
            </div>
          </WarningBar>
        </div>
      )}
      <div className="col-span-3 flex flex-row overflow-x-auto">
        {part?.images
          .filter((x) => x.useOnEbay)
          .map((image) => (
            <img
              key={image.id}
              src={image.url}
              className="max-w-[10rem] m-1 border border-slate-500 rounded-md shadow-md"
            />
          ))}
      </div>

      {/* Left Column */}
      <div className={`col-span-2 flex flex-col`}>
        {/* Tabs */}
        <RadioGroup value={viewType} onChange={setViewType} className="my-4">
          <RadioGroup.Label className="sr-only">
            {t("Choose which view to display")}
          </RadioGroup.Label>
          <div
            className={classNames(
              "grid gap-3",
              canManageListings ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-3 lg:grid-cols-3"
            )}
          >
            {viewTypes.map((type) => (
              <RadioGroup.Option
                key={`${type.value}`}
                value={type.value}
                className={({ active, checked }) =>
                  classNames(
                    active ? "ring-2 ring-offset-2 ring-primary-500" : "",
                    checked
                      ? "bg-primary-600 border-transparent text-white hover:bg-primary-700"
                      : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                    "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">{type.label}</RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        <>
          {
            {
              [TabIndex.Information]: (
                <StockInformation
                  part={part!}
                  onGoToListing={canManageListings ? () => setViewType(TabIndex.Listing) : undefined}
                />
              ),
              [TabIndex.Listing]: (
                <EditListing part={part!} page={listingPage} setPage={setListingPage} />
              ),
              [TabIndex.Images]: <EditListingImages part={part!} />,
              [TabIndex.Account]: invoice?.data?.data?.account ? <AccountDetails account={invoice.data.data.account} /> : <>{t("No linked account")}</>,
            }[viewType]
          }
        </>
      </div>

      {/* Right Column */}
      <div className="col-span-1 gap-6 flex flex-col mt-4">
        {viewType === TabIndex.Listing && <ListingSidebar part={part!} />}
        <LinkedWorkOrdersCard linkedItems={workOrderItems} />
        <StockPrintLabels part={part!} />
        <EbayPartMessages part={part!} />
        <PartProPartMessages part={part!} />
        <Card title={t("Part Actions")} bodyClassName="p-4">
          <div className="flex flex-col gap-4">
            <UnlistedDetailsCard fullPart={part!} />
            <ButtonInput
              label={t("Part History")}
              isSubmit={false}
              onClick={() => setHistoryOpen(true)}
              classes="!bg-slate-600 hover:!bg-slate-700"
            />
            {part?.partStatus === PartStatus.Sold && (
              <ButtonInput
                label={t("Return Part")}
                isSubmit={false}
                onClick={() => setReturnOpen(true)}
                classes="!bg-amber-500 hover:!bg-amber-600"
              />
            )}
            {!isDeletedPart && (
              <ButtonInput
                label={t("Delete Part")}
                isSubmit={false}
                onClick={() => setDeleteOpen(true)}
                classes="!bg-red-600 hover:!bg-red-700"
              />
            )}
          </div>
        </Card>
        <PartHistoryDrawer open={historyOpen} setOpen={setHistoryOpen} partId={part.id} />
      </div>
    </div>
  );
}